import { compact } from 'lodash';
import { ConversationMessageType } from 'constants/conversation';
import { ApplicableTransactionAction, AdditionalTransactionAction } from 'constants/transaction';
import { transformPhotoDto } from 'data/transformers/photo';
import { transformCarrierDto } from 'data/transformers/carrier';
import { transformDropOffType } from 'data/transformers/drop-off';
import { transformReturnShippingOptionDto } from 'data/transformers/return-shipping-option';
import { getUserThumbnail } from 'data/utils/user';
import { transformCurrencyAmountDto } from 'data/transformers/currency-amount';
import { transformItemPhotoDto } from 'data/transformers/item';
export const transformEducationDto = education => {
  const {
    action,
    action_title,
    screen_title,
    type,
    url
  } = education;
  return {
    action,
    actionTitle: action_title,
    screenTitle: screen_title,
    type,
    url
  };
};

const transformMessage = message => {
  switch (message.entity_type) {
    case ConversationMessageType.ActionMessage:
    case ConversationMessageType.StatusMessage:
      {
        const entity = message.entity;
        return {
          style: entity.template.style,
          title: entity.title,
          subtitle: entity.subtitle,
          notification: entity.notification,
          actions: entity.actions
        };
      }

    case ConversationMessageType.OfferMessage:
      {
        const entity = message.entity;
        return {
          note: entity.note,
          price: entity.price,
          isPriceFixed: entity.price_fixed,
          originalPrice: entity.original_price,
          currency: entity.currency,
          isCurrentUser: entity.current,
          userId: entity.user_id.toString()
        };
      }

    case ConversationMessageType.OfferRequestMessage:
      {
        const entity = message.entity;
        return {
          userId: entity.user_id.toString(),
          status: entity.status,
          price: entity.price,
          isPriceFixed: entity.price_fixed,
          originalPrice: entity.original_price,
          currency: entity.currency,
          statusTitle: entity.status_title,
          offerRequestId: entity.offer_request_id.toString(),
          isCurrentUser: entity.current,
          title: entity.title,
          body: entity.body,
          transactionId: entity.transaction_id.toString()
        };
      }

    case ConversationMessageType.Message:
      {
        const entity = message.entity;
        return {
          id: entity.id,
          content: entity.body,
          isSentFromMobile: entity.sent_via_mobile,
          photos: entity.photos.map(transformPhotoDto),
          isHidden: entity.is_hidden,
          userId: String(entity.user_id),
          reaction: entity.reaction
        };
      }

    case ConversationMessageType.PortalMessage:
      {
        const entity = message.entity;
        return {
          id: entity.id,
          content: entity.body,
          isSentFromMobile: entity.sent_via_mobile,
          photos: entity.photos.map(transformPhotoDto),
          isHidden: entity.is_hidden
        };
      }

    case ConversationMessageType.ReportSuggestion:
    default:
      return null;
  }
};

const transformMessageEntity = message => ({
  createdTime: message.created_at_ts,
  createdRelativeTime: message.created_time_ago,
  entityType: message.entity_type,
  message: transformMessage(message)
});

const transformOfferPriceSuggestion = _ref => {
  let {
    label,
    price
  } = _ref;
  return {
    label,
    price: transformCurrencyAmountDto(price)
  };
};

export const transformOfferRequestOptions = response => {
  var _response$offer_sugge;

  return {
    remaining: response.remaining,
    maxDiscount: response.max_discount,
    canMakeMore: response.can_make_more,
    minPrice: transformCurrencyAmountDto(response.min_price),
    maxPrice: transformCurrencyAmountDto(response.max_price),
    offerSuggestions: ((_response$offer_sugge = response.offer_suggestions) === null || _response$offer_sugge === void 0 ? void 0 : _response$offer_sugge.map(transformOfferPriceSuggestion)) || [],
    feesReductionThreshold: response.fees_reduction_threshold ? transformCurrencyAmountDto(response.fees_reduction_threshold) : null
  };
};
export const transformConversationItemDto = item => ({
  id: item.id,
  priceNumeric: item.price.amount,
  currency: item.price.currency_code,
  url: item.url,
  photo: item.photos.length ? transformItemPhotoDto(item.photos[0]) : null
});
export const transformEstimatedDetail = estimatedDetail => ({
  status: estimatedDetail.status,
  timePeriod: estimatedDetail.time_period,
  message: estimatedDetail.message
});
export const transformCurrentCarrier = currentCarrier => ({
  id: currentCarrier.id,
  priority: currentCarrier.priority,
  name: currentCarrier.name,
  trackingCode: currentCarrier.tracking_code,
  trackingUrl: currentCarrier.tracking_url
});
export const transformTrackingJourneySummaryCarrier = carrier => ({
  id: carrier.id,
  logoUrl: carrier.logo_url,
  priority: carrier.priority,
  trackingCode: carrier.tracking_code,
  trackingUrl: carrier.tracking_url
});
export const transformTrackingJourneySummaryDetails = trackingDetail => ({
  message: trackingDetail.message,
  timestamp: trackingDetail.timestamp,
  carrier: trackingDetail.carrier ? transformTrackingJourneySummaryCarrier(trackingDetail.carrier) : null,
  cellType: trackingDetail.cell_type
});
export const transformTrackingJourneySummary = response => ({
  details: response.details.map(transformTrackingJourneySummaryDetails),
  canMarkAsReceived: response.actions.includes('mark_as_received'),
  canMarkAsReturnedToSender: response.actions.includes('complete_return_to_sender'),
  currentCarrier: response.current_carrier && transformCurrentCarrier(response.current_carrier),
  estimatedDetail: response.estimated_detail && transformEstimatedDetail(response.estimated_detail),
  userSide: response.user_side
});
export const transformLabelFormatDetails = labelFormatDetail => ({
  iconUrl: labelFormatDetail.icon_url,
  title: labelFormatDetail.title,
  subtitle: labelFormatDetail.subtitle
});
export const transformShipmentInstructions = response => ({
  carrier: transformCarrierDto(response.carrier),
  carrierCode: response.carrier_code,
  hint: response.hint,
  id: response.id,
  instructions: response.instructions,
  currency: response.currency,
  price: response.price,
  priceTitle: response.price_title,
  receiverAddress: response.receiver_address,
  receiverFullAddress: response.receiver_full_address,
  receiverName: response.receiver_name,
  receiverTitle: response.receiver_title,
  receiverPhoto: response.receiver_photo && transformPhotoDto(response.receiver_photo),
  senderPhoto: response.sender_photo && transformPhotoDto(response.sender_photo),
  dropOffType: response.drop_off_type && transformDropOffType(response.drop_off_type),
  labelFormatDetails: response.label_format_details && transformLabelFormatDetails(response.label_format_details),
  returnShippingOption: response.return_shipping_option && transformReturnShippingOptionDto(response.return_shipping_option),
  actionTitle: response.action_title,
  screenTitle: response.screen_title,
  shippingPrice: transformCurrencyAmountDto(response.shipping_price),
  dropOffMapEnabled: response.drop_off_map_enabled
});
export const transformDigitalLabelItem = response => ({
  name: response.name,
  imageUrl: response.image_url
});
export const transformDigitalLabelCarrier = response => ({
  name: response.name,
  iconUrl: response.icon_url
});
export const transformDigitalLabel = response => ({
  code: response.code,
  imageUrl: response.image_url,
  carrier: transformDigitalLabelCarrier(response.carrier),
  items: response.items.map(transformDigitalLabelItem),
  receiverName: response.receiver_name,
  deliveryEstimate: response.delivery_estimate,
  expirationDate: response.expiration_date,
  downloadUrl: response.download_url,
  digitalLabelTitle: response.digital_label_title,
  labelExpiresTitle: response.label_expires_title,
  downloadLabelTitle: response.download_label_title,
  headerSubtitle: response.header_subtitle
});

const transformConversationOppositeUser = oppositeUser => ({
  id: oppositeUser.id.toString(),
  city: null,
  localCountryTitle: '',
  isLocationExposed: null,
  login: oppositeUser.login,
  lastLoginTime: oppositeUser.last_logged_in_at,
  feedbackCount: null,
  reviewCount: oppositeUser.review_count,
  feedbackReputation: oppositeUser.feedback_reputation,
  profileUrl: oppositeUser.profile_url,
  photo: oppositeUser.photo ? transformPhotoDto(oppositeUser.photo) : null,
  photoThumbnail: getUserThumbnail(oppositeUser.photo),
  isSystem: oppositeUser.is_system,
  isHated: oppositeUser.is_hated,
  locationDescription: oppositeUser.location_description,
  isUserUnblockable: oppositeUser.is_user_unblockable
});

export const transformConversationTransaction = transaction => ({
  id: transaction.id,
  order: {
    reserved: transaction.is_reserved,
    itemIds: transaction.item_ids,
    title: transaction.item_title
  },
  buyer: {
    id: transaction.buyer_id,
    business: null
  },
  userSide: transaction.current_user_side,
  shipment: transaction.shipment_id ? {
    id: transaction.shipment_id,
    status: transaction.shipment_status,
    packageType: {
      packageSizeCode: transaction.package_size_code
    },
    price: transaction.shipment_price ? transaction.shipment_price.amount : null,
    currency: transaction.shipment_price ? transaction.shipment_price.currency_code : null
  } : null,
  canMarkAsReceived: transaction.available_actions.includes(AdditionalTransactionAction.MarkAsReceived),
  availableActions: transaction.available_actions,
  itemsCount: transaction.item_count,
  itemId: transaction.item_id,
  isItemVerified: transaction.offline_verification,
  offer: {
    id: transaction.offer_id,
    price: transformCurrencyAmountDto(transaction.offer_price)
  },
  status: transaction.status,
  itemTitle: transaction.item_title,
  seller: transaction.seller_id ? {
    id: transaction.seller_id,
    business: transaction.is_business_seller
  } : null
});

const transformConversationModeratedItemDto = moderatedItem => ({
  id: moderatedItem.id,
  title: moderatedItem.title,
  status: moderatedItem.status,
  availabilityStatus: moderatedItem.availability_status,
  size: moderatedItem.size,
  photoUrl: moderatedItem.photo_url
});

export const transformConversationResponse = response => {
  var _response$transaction, _response$transaction2, _response$transaction3, _response$transaction4, _response$transaction5, _response$transaction6, _response$transaction7, _response$safety_educ;

  return {
    id: response.id,
    messages: compact(response.messages.map(transformMessageEntity)),
    transactionId: (_response$transaction = response.transaction) === null || _response$transaction === void 0 ? void 0 : _response$transaction.id,
    isTranslated: response.translated,
    oppositeUser: response.opposite_user && transformConversationOppositeUser(response.opposite_user),
    buyerId: (_response$transaction2 = response.transaction) === null || _response$transaction2 === void 0 ? void 0 : _response$transaction2.buyer_id,
    isPaymentActionAvailable: (_response$transaction3 = (_response$transaction4 = response.transaction) === null || _response$transaction4 === void 0 ? void 0 : _response$transaction4.available_actions.includes(ApplicableTransactionAction.Buy || ApplicableTransactionAction.Offer || ApplicableTransactionAction.RequestOffer)) !== null && _response$transaction3 !== void 0 ? _response$transaction3 : false,
    translationType: response.localization,
    isReadByOppositeUser: response.read_by_opposite_user,
    itemId: (_response$transaction5 = response.transaction) === null || _response$transaction5 === void 0 ? void 0 : _response$transaction5.item_id,
    isReplyAllowed: response.allow_reply,
    isSuspicious: response.is_suspicious,
    isBundleAvailable: (_response$transaction6 = (_response$transaction7 = response.transaction) === null || _response$transaction7 === void 0 ? void 0 : _response$transaction7.available_actions.includes(AdditionalTransactionAction.Bundle)) !== null && _response$transaction6 !== void 0 ? _response$transaction6 : false,
    transaction: response.transaction ? transformConversationTransaction(response.transaction) : null,
    suggestedMessages: response.suggested_messages,
    item: response.transaction && {
      id: response.transaction.item_id,
      priceNumeric: response.transaction.offer_price.amount,
      currency: response.transaction.offer_price.currency_code,
      url: response.transaction.item_url,
      photo: response.transaction.item_photo ? transformItemPhotoDto(response.transaction.item_photo) : null
    },
    education: response.education ? transformEducationDto(response.education) : null,
    moderatedItems: response.moderated_items ? {
      itemCount: response.moderated_items.item_count,
      items: response.moderated_items.items.map(transformConversationModeratedItemDto)
    } : null,
    isControl: false,
    userHasSupportRole: false,
    isFirstTimeListerEducationRequired: (_response$safety_educ = response.safety_education) !== null && _response$safety_educ !== void 0 ? _response$safety_educ : false
  };
};